// import { apiOptimization } from './apiOptimization'; // Not used in this file - eslint-disable-line no-unused-vars

// Advanced caching service with multiple strategies
class CacheService {
  constructor() {
    this.memoryCache = new Map();
    this.cacheStrategies = {
      'memory': this.memoryCache,
      'session': sessionStorage,
      'local': localStorage
    };
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
    this.cacheHits = 0;
    this.cacheMisses = 0;
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.cacheEnabled = !this.isDevelopment; // Disable cache in development by default
  }

  // Set cache with strategy and TTL
  set(key, value, options = {}) {
    // Skip caching in development mode unless explicitly enabled
    if (!this.cacheEnabled) {
      if (this.isDevelopment) {
        console.log(`[CACHE DISABLED] Would cache: ${key}`);
      }
      return;
    }

    const {
      strategy = 'memory',
      ttl = this.defaultTTL,
      serialize = true
    } = options;

    const cacheItem = {
      value: serialize ? JSON.stringify(value) : value,
      timestamp: Date.now(),
      ttl,
      strategy
    };

    switch (strategy) {
      case 'memory':
        this.memoryCache.set(key, cacheItem);
        break;
      case 'session':
        sessionStorage.setItem(key, JSON.stringify(cacheItem));
        break;
      case 'local':
        localStorage.setItem(key, JSON.stringify(cacheItem));
        break;
      default:
        console.warn(`Unknown cache strategy: ${strategy}`);
        break;
    }
  }

  // Get from cache with strategy
  get(key, options = {}) {
    // Skip cache retrieval in development mode unless explicitly enabled
    if (!this.cacheEnabled) {
      if (this.isDevelopment) {
        console.log(`[CACHE DISABLED] Would retrieve: ${key}`);
      }
      return null;
    }

    const { strategy = 'memory', deserialize = true } = options;
    
    let cacheItem;
    
    switch (strategy) {
      case 'memory':
        cacheItem = this.memoryCache.get(key);
        break;
      case 'session':
        const sessionData = sessionStorage.getItem(key);
        cacheItem = sessionData ? JSON.parse(sessionData) : null;
        break;
      case 'local':
        const localData = localStorage.getItem(key);
        cacheItem = localData ? JSON.parse(localData) : null;
        break;
      default:
        console.warn(`Unknown cache strategy: ${strategy}`);
        cacheItem = null;
        break;
    }

    if (!cacheItem) {
      this.cacheMisses++;
      return null;
    }

    // Check if expired
    if (Date.now() - cacheItem.timestamp > cacheItem.ttl) {
      this.delete(key, { strategy });
      this.cacheMisses++;
      return null;
    }

    this.cacheHits++;
    return deserialize ? JSON.parse(cacheItem.value) : cacheItem.value;
  }

  // Delete from cache
  delete(key, options = {}) {
    const { strategy = 'memory' } = options;
    
    switch (strategy) {
      case 'memory':
        this.memoryCache.delete(key);
        break;
      case 'session':
        sessionStorage.removeItem(key);
        break;
      case 'local':
        localStorage.removeItem(key);
        break;
      default:
        console.warn(`Unknown cache strategy: ${strategy}`);
        break;
    }
  }

  // Clear all cache
  clear(strategy = null) {
    if (strategy) {
      switch (strategy) {
        case 'memory':
          this.memoryCache.clear();
          break;
        case 'session':
          sessionStorage.clear();
          break;
        case 'local':
          localStorage.clear();
          break;
        default:
          console.warn(`Unknown cache strategy: ${strategy}`);
          break;
      }
    } else {
      this.memoryCache.clear();
      sessionStorage.clear();
      localStorage.clear();
    }
  }

  // Get cache statistics
  getCacheStats() {
    return {
      size: this.memoryCache.size,
      keys: Array.from(this.memoryCache.keys()),
      hitRate: this.cacheHits / (this.cacheHits + this.cacheMisses) || 0,
      cacheHits: this.cacheHits || 0,
      cacheMisses: this.cacheMisses || 0,
      enabled: this.cacheEnabled,
      isDevelopment: this.isDevelopment
    };
  }

  // Development helpers
  enableCache() {
    this.cacheEnabled = true;
    if (this.isDevelopment) {
      console.log('✅ Cache enabled');
    }
  }

  disableCache() {
    this.cacheEnabled = false;
    if (this.isDevelopment) {
      console.log('❌ Cache disabled');
    }
  }

  toggleCache() {
    this.cacheEnabled = !this.cacheEnabled;
    if (this.isDevelopment) {
      console.log(`🔄 Cache ${this.cacheEnabled ? 'ENABLED' : 'DISABLED'}`);
    }
    return this.cacheEnabled;
  }

  // Development status
  devStatus() {
    if (this.isDevelopment) {
      console.log('📊 Cache Status:', this.getCacheStats());
    }
  }

  // Cache with automatic invalidation
  async cacheWithInvalidation(key, fetcher, options = {}) {
    const {
      strategy = 'memory',
      ttl = this.defaultTTL,
      invalidateOn = []
    } = options;

    // Check cache first
    const cached = this.get(key, { strategy });
    if (cached) {
      return cached;
    }

    // Fetch fresh data
    const data = await fetcher();
    
    // Cache the data
    this.set(key, data, { strategy, ttl });

    // Set up invalidation listeners
    invalidateOn.forEach(event => {
      const listener = () => this.delete(key, { strategy });
      window.addEventListener(event, listener);
      
      // Store listener for cleanup
      if (!this.invalidationListeners) {
        this.invalidationListeners = new Map();
      }
      this.invalidationListeners.set(key, listener);
    });

    return data;
  }

  // Smart cache for API responses
  async smartCache(endpoint, params, fetcher, options = {}) {
    const cacheKey = `api:${endpoint}:${JSON.stringify(params)}`;
    
    return this.cacheWithInvalidation(
      cacheKey,
      fetcher,
      {
        strategy: 'memory',
        ttl: options.ttl || this.defaultTTL,
        invalidateOn: options.invalidateOn || ['user-logout', 'data-updated']
      }
    );
  }
}

// Export singleton instance
export const cacheService = new CacheService();

// Cache decorators for functions
export const cacheable = (options = {}) => {
  return (target, propertyKey, descriptor) => {
    const originalMethod = descriptor.value;
    
    descriptor.value = async function(...args) {
      const cacheKey = `${target.constructor.name}:${propertyKey}:${JSON.stringify(args)}`;
      
      const cached = cacheService.get(cacheKey, { strategy: options.strategy });
      if (cached) {
        return cached;
      }
      
      const result = await originalMethod.apply(this, args);
      cacheService.set(cacheKey, result, options);
      
      return result;
    };
    
    return descriptor;
  };
};

// Cache invalidation helpers
export const cacheInvalidation = {
  // Invalidate user-related cache
  invalidateUserCache: (userId) => {
    const patterns = [
      `api:user:*`,
      `api:profile:*`,
      `api:equity:*`,
      `api:dataroom:*`
    ];
    
    patterns.forEach(pattern => {
      cacheService.clear(pattern);
    });
  },

  // Invalidate specific data type
  invalidateDataType: (dataType) => {
    const pattern = `api:${dataType}:*`;
    cacheService.clear(pattern);
  },

  // Invalidate all API cache
  invalidateAllAPI: () => {
    cacheService.clear('memory');
  }
};

export default cacheService;
